import {
    ActionTypes,
    AdminRoleId,
    doesWorkflowRejectCodeExist,
    getWorkflowRejectCodeMessage
} from '@tassat/common-definitions';
import { IFormValues } from '@tassat/forms/src/AdminUserForm/types';
import {
    CustomerAddUserReq,
    CustomerModifyUserReq,
    UserRecord,
    UserValidationRecord
} from '@tassat/fred-definitions';
import React from 'react';

import history from 'Lib/browserHistory';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { createAndMapPcgUser, modifyUserPcgMapping } from 'Thunks/user/pcgAdmin';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const submitAdminUserForm = async (
    values: IFormValues,
    opts: {
        actionType: ActionTypes;
        initialValues: IFormValues;
        isAdmin: boolean;
        firmId: string;
        editUser: UserRecord | UserValidationRecord;
        features?: {
            sso?: boolean;
        };
        onClose: () => void;
        setSubmitError: React.Dispatch<React.SetStateAction<string>>;
    }
) => {
    const {
        actionType,
        initialValues,
        isAdmin,
        firmId,
        editUser,
        onClose,
        setSubmitError
    } = opts;

    try {
        let response = { data: undefined };

        if (actionType === ActionTypes.add) {
            const userValues: Omit<
                CustomerAddUserReq,
                'UUID' |
                'RequestId' |
                'RequesterId' |
                'RequestType' |
                'RequestPattern' |
                'FirmId' |
                'FirmId' |
                'IsTokenAPIUser' |
                'IsAdminAPIUser'
            > = {
                FirstName: values.FirstName,
                LastName: values.LastName,
                Email: values.Email,
                Username: values.Username,
                Phone: values.Phone,
                IsAdmin: isAdmin,
                IsApprover: false,
                Notes: values.Notes,
                AdminRoleId: values.AdminRoleId.value.toString()
            };
            userValues.Email = userValues.Email.trim();
            if (opts.features.sso){
                userValues.Username = values.Username.trim();
            }

            if (userValues.AdminRoleId === AdminRoleId.PCG_ADMIN) {
                // TODDO: Change when we support ACG Admin
                response = await createAndMapPcgUser(firmId, {
                    ...userValues,
                    PCGMapping: values.PCGMapping
                });
            } else {
                response = await Worker.getInstance()
                    .createUser(firmId, userValues);
            }
        } else if (actionType === ActionTypes.edit && 'UserId' in editUser) {
            const userValues: Omit<
                CustomerModifyUserReq,
                'UUID' |
                'RequestId' |
                'RequesterId' |
                'FirmId' |
                'ResetPassword' |
                'IsTwoFactorComplete' |
                'IsTermsOfServiceApproved' |
                'IsTokenAPIUser' |
                'IsAdminAPIUser' |
                'IsFrozen' |
                'IsLocked' |
                'ResetMfa' |
                'EditUsername'
            > = {
                UserId: editUser.UserId,
                FirstName: values.FirstName,
                LastName: values.LastName,
                Email: values.Email,
                Phone: values.Phone,
                Notes: values.Notes,
                IsAdmin: isAdmin,
                IsApprover: false,
                AdminRoleId: values.AdminRoleId.value.toString()
            };
            const PCGMapping = values.PCGMapping;
            response = await Worker.getInstance()
                .updateUser(firmId, userValues);
            if (userValues.AdminRoleId === AdminRoleId.PCG_ADMIN) {
                await modifyUserPcgMapping(userValues.UserId, PCGMapping);
            } else if (initialValues.AdminRoleId.value === AdminRoleId.PCG_ADMIN && userValues.AdminRoleId !== AdminRoleId.PCG_ADMIN) {
                await modifyUserPcgMapping(userValues.UserId, []);
            }
        } else if (actionType === ActionTypes.sso_validate && 'UserValidationId' in editUser){
            const userValues = {
                UserValidationId: editUser.UserValidationId,
                FirstName: values.FirstName,
                LastName: values.LastName,
                Email: values.Email,
                Phone: values.Phone,
                Notes: values.Notes,
                IsAdmin: isAdmin,
                IsApprover: false,
                AdminRoleId: values.AdminRoleId.value,
                Username: values.Username,
                PCGMapping: values.PCGMapping,
                PCGId: null,
                FirmId: firmId
            };

            // The PCGId is for sso_validate will be only one mapping and its value.
            if (userValues.AdminRoleId === AdminRoleId.PCG_ADMIN) {
                userValues.PCGId = userValues.PCGMapping['value'];
            } else {
                delete userValues.PCGId;
            }
            delete userValues.PCGMapping;
            response = await Worker.getInstance()
                .approveUserSsoPendingRequest(userValues);
        }

        if (response.data && response.data.Status === 48) {
            // Redirect to the new user if New Record ID is present, otherwise close modal
            if (actionType === ActionTypes.add) {
                history.push(`/admin/users${response.data.NewRecordId ? `/${response.data.NewRecordId}` : ''}`);
            } else {
                onClose();
            }
        } else if (response.data && response.data.Status === 49) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} user: ${response.data.Reason || 'Something went wrong'}`);
        } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} user: ${getWorkflowRejectCodeMessage(response.data.Status)}`);
        }
    } catch (error) {
        setSubmitError('Something went wrong');
    }
};